import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'usehooks-ts'

/**
 * Поиск-фильтр списка, синхронизированный с URL (?search=), с дебаунсом.
 * Возвращает [value, setValue, debounced]:
 *   - value      — мгновенное значение для контролируемого инпута
 *   - setValue   — onChange инпута
 *   - debounced  — то, что идёт в query-key/запрос (как раньше debouncedSearch)
 *
 * Дебаунс-значение пишется в URL (replace, без замусоривания истории) и сбрасывает
 * пагинацию (?page=). Начальное значение берётся из URL — ссылку с поиском можно
 * расшарить. Первый рендер не трогает URL, чтобы не сбить page из расшаренной ссылки.
 */
export const useSearchParamState = (
	param = 'search',
	debounceMs = 500
): readonly [string, (value: string) => void, string] => {
	const [searchParams, setSearchParams] = useSearchParams()
	const [value, setValue] = useState(searchParams.get(param) ?? '')
	const debounced = useDebounce(value, debounceMs)
	const isFirstRun = useRef(true)

	useEffect(() => {
		if (isFirstRun.current) {
			isFirstRun.current = false
			return
		}

		setSearchParams(
			prev => {
				if (debounced) {
					prev.set(param, debounced)
				} else {
					prev.delete(param)
				}
				prev.delete('page')
				return prev
			},
			{ replace: true }
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debounced])

	return [value, setValue, debounced] as const
}
